import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Room from '@mui/icons-material/Room'

// Named, searchable markers pinned to the event map ("Green Campsite",
// "Gate C"...). Rendering + the create/edit dialog live here; state and API
// calls stay in EventControlMap.

export const PIN_COLORS = [
  '#16A34A',
  '#DC2626',
  '#2563EB',
  '#EA580C',
  '#7C3AED',
  '#0D9488',
  '#DB2777',
  '#CA8A04',
  '#475569',
  '#92400E',
]

export const DEFAULT_PIN_COLOR = PIN_COLORS[0]

// The wrapper is positioned with translate(-50%, -100%) so the icon's tip
// sits exactly on the pin's coordinates.
export const MapPinMarker = ({ pin, highlighted }) => {
  const color = pin.color || DEFAULT_PIN_COLOR

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          padding: '2px 7px',
          borderRadius: '6px',
          backgroundColor: 'rgba(15,23,42,0.92)',
          border: `1px solid ${highlighted ? 'white' : 'rgba(255,255,255,0.22)'}`,
          boxShadow: highlighted
            ? `0 0 10px ${color}, 0 4px 10px rgba(0,0,0,0.4)`
            : '0 4px 10px rgba(0,0,0,0.35)',
          maxWidth: 160,
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontSize: '0.66rem',
            fontWeight: 900,
            lineHeight: 1.2,
          }}
          noWrap
        >
          {pin.label}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: '-1px',
        }}
      >
        {highlighted && (
          <Box
            sx={{
              position: 'absolute',
              width: 30,
              height: 30,
              borderRadius: '50%',
              backgroundColor: color,
              opacity: 0.4,
              pointerEvents: 'none',
              animation: 'cadPinPulse 1.6s ease-out infinite',
              '@keyframes cadPinPulse': {
                '0%': { transform: 'scale(0.5)', opacity: 0.5 },
                '70%': { transform: 'scale(1.8)', opacity: 0 },
                '100%': { transform: 'scale(1.8)', opacity: 0 },
              },
            }}
          />
        )}
        <Room
          sx={{
            fontSize: 28,
            color,
            filter:
              'drop-shadow(0 0 1px rgba(255,255,255,0.9)) drop-shadow(0 0 1px rgba(255,255,255,0.9)) drop-shadow(0 2px 4px rgba(0,0,0,0.45))',
          }}
        />
      </Box>
    </Box>
  )
}

export const MapPinDialog = ({ open, pin, onSave, onDelete, onClose }) => {
  const [label, setLabel] = useState('')
  const [color, setColor] = useState(DEFAULT_PIN_COLOR)
  const [saving, setSaving] = useState(false)
  const isEdit = !!pin?.id

  useEffect(() => {
    if (!open) return
    setLabel(pin?.label ?? '')
    setColor(pin?.color || DEFAULT_PIN_COLOR)
    setSaving(false)
  }, [open, pin])

  const save = async () => {
    const trimmed = label.trim()
    if (!trimmed) return
    setSaving(true)
    try {
      await onSave({ label: trimmed, color })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Map Pin' : 'Add Map Pin'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Green Campsite"
            inputProps={{ maxLength: 60 }}
            autoFocus
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
            }}
          />
          <Stack gap={0.75}>
            <Typography variant="body2" color="text.secondary">
              Colour
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
              {PIN_COLORS.map((option) => (
                <Tooltip key={option} title={option}>
                  <Box
                    onClick={() => setColor(option)}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      backgroundColor: option,
                      cursor: 'pointer',
                      border:
                        color === option
                          ? '3px solid #0F172A'
                          : '3px solid transparent',
                      boxShadow:
                        color === option
                          ? '0 0 0 2px white inset'
                          : '0 1px 4px rgba(0,0,0,0.25)',
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isEdit && (
          <Button color="error" onClick={onDelete} sx={{ mr: 'auto' }}>
            Delete
          </Button>
        )}
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!label.trim() || saving}
        >
          {isEdit ? 'Save' : 'Add Pin'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
